import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import FileDisplayItem from './FileDisplayItem';

export interface UploadedFile {
    id: string;
    name: string;
    type: string;
    size: number;
    content: string | ArrayBuffer;
    isText?: boolean;
}

interface ChatInputProps {
    onSubmit: (message: string, files?: UploadedFile[], searchEnabled?: boolean) => void;
    onStopGeneration: () => void;
    disabled?: boolean;
    isStreaming?: boolean;
    placeholder?: string;
    errorState?: boolean;
    initialValue?: string;
    isEditing?: boolean;
    onCancelEdit?: () => void;
    onCopyText?: (text: string) => void;
}

const ChatInput: React.FC<ChatInputProps & { searchEnabled: boolean; onSearchToggle: () => void }> = ({
    onSubmit,
    onStopGeneration,
    disabled = false,
    isStreaming = false,
    placeholder = 'Type your message here...',
    errorState = false,
    searchEnabled = false,
    onSearchToggle,
    initialValue = '',
    isEditing = false,
    onCancelEdit,
    onCopyText
}) => {
    const [input, setInput] = useState(initialValue);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isCopied, setIsCopied] = useState(false);

    // Update input when initialValue changes (for editing)
    useEffect(() => {
        if (initialValue) {
            setInput(initialValue);
            // Ensure textarea is resized for the new content
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
                const newHeight = Math.min(textareaRef.current.scrollHeight, 48);
                textareaRef.current.style.height = `${newHeight}px`;
            }
        }
    }, [initialValue]);

    // Auto-resize textarea based on content
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';

        // Set the height to scrollHeight
        const newHeight = Math.min(textarea.scrollHeight, 48); // Limit height to match design
        textarea.style.height = `${newHeight}px`;
    }, [input]);

    // Reset when disabled state changes to ensure proper state management
    useEffect(() => {
        if (!disabled) {
            setIsSubmitting(false);
        }
    }, [disabled]);

    // Add a console log to debug submission status
    useEffect(() => {
        console.log('ChatInput - isSubmitting:', isSubmitting, 'disabled prop:', disabled, 'isStreaming:', isStreaming);
    }, [isSubmitting, disabled, isStreaming]);

    const generateFileId = () => {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedInput = input.trim();
        // Prevent submission if disabled, streaming, submitting, or empty input/files
        if ((!trimmedInput && uploadedFiles.length === 0) || disabled || isStreaming || isSubmitting) return;

        try {
            setIsSubmitting(true);
            await onSubmit(trimmedInput, uploadedFiles.length > 0 ? uploadedFiles : undefined, searchEnabled);
            // Clear input and files after successful submission
            setInput('');
            setUploadedFiles([]);

            // Reset editing state if needed
            if (isEditing && onCancelEdit) {
                onCancelEdit();
            }
        } catch (error) {
            console.error('Error submitting message:', error);
            // Keep the input so user doesn't lose their message
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Submit on Enter without Shift, only if not streaming
        if (e.key === 'Enter' && !e.shiftKey && !isStreaming) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Handle files
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) handleFileObject(file);
            }
            // Handle large text (over 1000 tokens, approximately 4000 characters)
            else if (item.kind === 'string' && input.length > 4000) {
                e.preventDefault();
                const text = e.clipboardData.getData('text/plain');
                const textFile = new File([text], 'pasted-text.txt', { type: 'text/plain' });
                handleFileObject(textFile);
            }
        }
    };

    const handleFileObject = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result;
            if (result) {
                const fileType = file.type || 'application/octet-stream';
                const fileName = file.name.toLowerCase();
                // Special handling for package.json files
                const isPackageJson = fileName === 'package.json';
                const isJsonFile = fileType === 'application/json' ||
                    fileName.endsWith('.json') ||
                    isPackageJson;

                let processedContent = result;

                // For JSON files, especially package.json, ensure proper parsing
                if (isJsonFile && typeof result === 'string') {
                    try {
                        // Validate JSON by parsing and stringifying with formatting
                        const jsonObj = JSON.parse(result);
                        processedContent = JSON.stringify(jsonObj, null, 2);
                    } catch (err) {
                        console.error('Error parsing JSON file:', err);
                        // Keep original content if parsing fails
                    }
                }

                setUploadedFiles(prev => [
                    ...prev,
                    {
                        id: generateFileId(),
                        name: file.name,
                        type: isJsonFile ? 'application/json' : fileType,
                        size: file.size,
                        content: processedContent,
                        isText: isJsonFile || fileType.startsWith('text/')
                    }
                ]);
            }
        };

        const fileName = file.name.toLowerCase();
        if (file.type.startsWith('text/') ||
            file.type === 'application/json' ||
            fileName.endsWith('.json')) {
            reader.readAsText(file, 'UTF-8'); // Use UTF-8 encoding for text files
        } else {
            reader.readAsDataURL(file);
        }
    };

    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            for (let i = 0; i < files.length; i++) {
                handleFileObject(files[i]);
            }
        }
        // Reset the input to allow uploading the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (id: string) => {
        setUploadedFiles(uploadedFiles.filter(file => file.id !== id));
    };

    // Determine if we're currently in searching mode
    const isSearching = isStreaming && placeholder?.includes('searching') || false;

    // Determine if the main submit button should be disabled
    const isSubmitDisabled = disabled || isSubmitting || isStreaming || (!input.trim() && uploadedFiles.length === 0);

    // Handle cancel edit button press
    const handleCancelEdit = () => {
        if (onCancelEdit) {
            setInput('');
            onCancelEdit();
        }
    };

    // Add function to copy current input to clipboard
    const handleCopyInput = () => {
        if (input.trim()) {
            if (onCopyText) {
                onCopyText(input);
                setIsCopied(true);

                // Reset after 2 seconds
                setTimeout(() => {
                    setIsCopied(false);
                }, 2000);
            } else {
                navigator.clipboard.writeText(input)
                    .then(() => {
                        // Show visual feedback
                        setIsCopied(true);

                        // Reset after 2 seconds
                        setTimeout(() => {
                            setIsCopied(false);
                        }, 2000);
                    })
                    .catch(err => {
                        console.error('Failed to copy input:', err);
                    });
            }
        }
    };

    // Add function to clear input
    const handleClearInput = () => {
        setInput('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.focus();
        }
    };

    return (
        <div className="space-y-2">
            {/* Uploaded files display */}
            {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {uploadedFiles.map(file => (
                        <FileDisplayItem
                            key={file.id}
                            file={file}
                            onRemove={handleRemoveFile}
                        />
                    ))}
                </div>
            )}

            {/* Prominent search indicator - only show if not already displayed in a message */}
            {isSearching && !document.querySelector('.thinking-container.active .shimmer-text') && (
                <div className="bg-blue-600/20 border border-blue-500/40 rounded-lg p-2 mb-2 text-center animate-pulse">
                    <div className="flex items-center justify-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                        <span className="font-medium text-blue-600 dark:text-blue-400">Searching the web for information...</span>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="relative flex items-center bg-gray-100 dark:bg-[#252525] rounded-full border border-gray-300 dark:border-[#3d3d3d] shadow-sm">
                    {/* Add button - disable while streaming */}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled || isStreaming}
                        className={`flex-shrink-0 h-10 w-10 flex items-center justify-center text-gray-500 dark:text-gray-300 ${disabled || isStreaming ? 'cursor-not-allowed opacity-50' : 'hover:text-gray-700 dark:hover:text-white'}`}
                    >
                        {isEditing ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M5 12L12 5M5 12L12 19" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        )}
                    </button>

                    {/* Input field and controls wrapper */}
                    <div className="flex-grow flex flex-col relative">
                        {/* Input edit actions - only show when input has content and not editing or streaming */}
                        {input.trim() && !isEditing && !isStreaming && (
                            <div className="absolute right-2 top-1 flex items-center space-x-1 opacity-80 z-10 bg-gray-50 dark:bg-gray-700/50 rounded px-1">
                                {/* Copy input button */}
                                <button
                                    type="button"
                                    onClick={handleCopyInput}
                                    title={isCopied ? "Copied!" : "Copy input"}
                                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300"
                                    aria-label="Copy message"
                                >
                                    {isCopied ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                        </svg>
                                    )}
                                </button>

                                {/* Clear input button */}
                                <button
                                    type="button"
                                    onClick={handleClearInput}
                                    title="Clear input"
                                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300"
                                    aria-label="Clear message"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* The textarea itself */}
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            disabled={disabled}
                            placeholder={placeholder}
                            rows={1}
                            className="flex-grow bg-transparent border-0 py-2 px-1 pr-14 focus:ring-0 focus:outline-none text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 resize-none overflow-hidden"
                        />
                    </div>

                    {/* Cancel edit button (only show when editing) */}
                    {isEditing && (
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="flex items-center justify-center gap-1 px-3 py-1.5 mx-1 rounded-full text-sm text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            <span>Cancel</span>
                        </button>
                    )}

                    {/* Search toggle button - disable while streaming */}
                    {!isEditing && (
                        <button
                            type="button"
                            onClick={onSearchToggle}
                            disabled={disabled || isStreaming}
                            className={`flex items-center justify-center gap-1 px-3 py-1.5 mx-1 rounded-full text-sm ${searchEnabled
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                                } ${disabled || isStreaming ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="2" y1="12" x2="22" y2="12"></line>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                            </svg>
                            <span>Search</span>
                        </button>
                    )}

                    {/* Conditional Buttons: Stop or Submit */}
                    {isStreaming ? (
                        <button
                            type="button"
                            onClick={onStopGeneration}
                            className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full mr-1 bg-red-500 hover:bg-red-600 text-white"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="6" y="6" width="12" height="12"></rect>
                            </svg>
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={isSubmitDisabled}
                            className={`flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full mr-1 
                                ${isSubmitDisabled
                                    ? 'text-gray-400 dark:text-gray-500 bg-gray-200 dark:bg-gray-700 cursor-not-allowed'
                                    : 'text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600'
                                }`}
                        >
                            {isEditing ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 13l4 4L19 7" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 19V5M5 12l7 7 7-7" />
                                </svg>
                            )}
                        </button>
                    )}
                </div>

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    className="hidden"
                    multiple
                    accept="*/*"
                />
            </form>
        </div>
    );
};

export default ChatInput; 